import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { ShieldCheck, Gauge, CheckCircle2, Anchor, LifeBuoy, FileSearch, AlertTriangle, ChevronDown, Languages } from 'lucide-react'
import { getSession } from '../lib/session'

// Language code → display name for the "languages served" proof.
const LANG_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', pt: 'Portuguese', it: 'Italian',
  zh: 'Chinese', ja: 'Japanese', ko: 'Korean', ru: 'Russian', ar: 'Arabic', hi: 'Hindi',
  th: 'Thai', he: 'Hebrew', el: 'Greek',
}

// Answer Trust & Audit — the reliability proof surfaced from real answer_evals:
// measured answer quality (0..1 groundedness), the verified rate, grounded rate,
// how often the deterministic safe fallback fired, a daily trend, a per-channel
// breakdown, recent flagged answers, and an expandable compliance audit trail.
// Renders nothing until there's traffic (honest 'baseline'), never blocks the page.

const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)}%`)
const dec = (x) => (x == null ? '—' : Number(x).toFixed(3))
const TONE = {
  good: 'text-green-700', warning: 'text-amber-700', critical: 'text-red-700', baseline: 'text-gray-400',
}

function Sparkline({ points }) {
  const vals = (points || []).map((p) => (p.avg == null ? 0 : p.avg))
  if (vals.length < 2) return null
  const w = 160, h = 36, max = 1, min = 0
  const step = w / (vals.length - 1)
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - ((v - min) / (max - min)) * h).toFixed(1)}`).join(' ')
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
    </svg>
  )
}

function ScoreCard({ icon: Icon, label, value, sub, tone = 'text-gray-900' }) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 text-gray-500 mb-3"><Icon size={16} /><span className="text-xs font-bold uppercase tracking-wide">{label}</span></div>
      <div className={`text-3xl font-bold tabular-nums ${tone}`}>{value}</div>
      {sub && <p className="mt-1.5 text-sm text-gray-500">{sub}</p>}
    </div>
  )
}

export default function GovernanceTrust() {
  const ns = getSession()?.namespaceId
  const [t, setT] = useState(null)
  const [openAudit, setOpenAudit] = useState(false)
  const [audit, setAudit] = useState(null)

  useEffect(() => {
    if (!ns) return
    axios.get('/api/v1/governance/trust?days=30').then((r) => setT(r.data)).catch(() => setT(null))
  }, [ns])

  const loadAudit = () => {
    if (audit) return
    axios.get('/api/v1/governance/audit-trail?limit=25').then((r) => setAudit(r.data)).catch(() => setAudit({ rows: [], total: 0 }))
  }

  if (!t) return null
  const toneCls = TONE[t.status] || TONE.baseline
  const baseline = !t.samples

  return (
    <section>
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <ShieldCheck size={16} className="text-primary-600" /> Answer Trust &amp; Audit
        </h3>
        <p className="text-sm text-gray-500">
          Every answer is graded for groundedness and guarded before it ships — the Verifier agent's verdict, measured over {t.window}.
          {!baseline && <> {t.samples.toLocaleString()} answers scored.</>}
        </p>
      </div>

      {baseline ? (
        <div className="card text-sm text-gray-500 flex items-center gap-2">
          <Gauge size={16} /> No answers scored yet — the trust proof builds itself as your bots answer. Nothing fabricated here.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <ScoreCard icon={Gauge} label="Answer quality" value={dec(t.avgQuality)} tone={toneCls}
              sub={<span className="inline-flex items-center gap-1">avg groundedness score <Sparkline points={t.trend} /></span>} />
            <ScoreCard icon={CheckCircle2} label="Verified rate" value={pct(t.verifiedRate)} tone={toneCls}
              sub="answers that passed every check" />
            <ScoreCard icon={Anchor} label="Grounded" value={pct(t.groundedRate)}
              sub="claims traced to a cited source" />
            <ScoreCard icon={LifeBuoy} label="Safe fallback" value={pct(t.fallbackRate)}
              sub="ungrounded claims auto-corrected" />
          </div>

          {/* Per-channel breakdown */}
          {t.byChannel?.length > 0 && (
            <div className="card mt-4">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">By channel</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2">
                {t.byChannel.map((c) => (
                  <div key={c.channel} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 capitalize">{c.channel}</span>
                    <span className="text-gray-500 tabular-nums">{dec(c.avg)} · {c.n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Languages served — the multilingual proof */}
          {t.languages?.length > 1 && (
            <div className="card mt-4">
              <div className="flex items-center gap-2 text-gray-500 mb-2"><Languages size={15} /><span className="text-xs font-bold uppercase tracking-wide">Languages served</span></div>
              <div className="flex flex-wrap gap-2">
                {t.languages.map((l) => (
                  <span key={l.lang} className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/10 text-primary-700 text-xs font-medium px-2.5 py-1">
                    {LANG_NAMES[l.lang] || l.lang} <span className="text-primary-400 tabular-nums">{l.n}</span>
                  </span>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-gray-400">Detected automatically per visitor — answered in their language, no separate setup.</p>
            </div>
          )}

          {/* Recent flagged answers */}
          {t.recentFlags?.length > 0 && (
            <div className="card mt-4 border-amber-500/20 bg-amber-500/[0.03]">
              <div className="flex items-center gap-2 text-amber-700 mb-2"><AlertTriangle size={15} /><span className="text-xs font-bold uppercase tracking-wide">Recently flagged</span></div>
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {t.recentFlags.map((f) => (
                  <div key={f.id} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                    <div className="text-gray-700">“{f.query || '—'}”</div>
                    <div className="text-gray-400 mt-0.5">
                      {(Array.isArray(f.issues) ? f.issues : []).join(' · ') || 'unverified'}
                      {f.qualityScore != null && <> · score {dec(f.qualityScore)}</>}
                      {f.channel && <> · {f.channel}</>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expandable compliance audit trail */}
          <div className="mt-4">
            <button
              onClick={() => { setOpenAudit((o) => !o); loadAudit() }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:underline">
              <FileSearch size={15} /> Compliance audit trail
              <ChevronDown size={14} className={`transition-transform ${openAudit ? 'rotate-180' : ''}`} />
            </button>
            {openAudit && (
              <div className="card mt-2 overflow-x-auto">
                {!audit ? (
                  <p className="text-sm text-gray-400">Loading…</p>
                ) : audit.rows.length === 0 ? (
                  <p className="text-sm text-gray-400">No records yet.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 text-left border-b border-gray-100">
                        <th className="py-1.5 pr-3 font-medium">When</th>
                        <th className="py-1.5 pr-3 font-medium">Channel</th>
                        <th className="py-1.5 pr-3 font-medium">Query (PII-redacted)</th>
                        <th className="py-1.5 pr-3 font-medium">Score</th>
                        <th className="py-1.5 pr-3 font-medium">Verified</th>
                        <th className="py-1.5 pr-3 font-medium">Model</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.rows.map((r) => (
                        <tr key={r.id} className="border-b border-gray-50">
                          <td className="py-1.5 pr-3 text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td className="py-1.5 pr-3 text-gray-600 capitalize">{r.channel || '—'}</td>
                          <td className="py-1.5 pr-3 text-gray-700 max-w-[280px] truncate">{r.query || '—'}</td>
                          <td className="py-1.5 pr-3 tabular-nums text-gray-700">{dec(r.quality_score)}</td>
                          <td className="py-1.5 pr-3">
                            {r.verified
                              ? <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> yes</span>
                              : <span className="text-amber-700">flagged</span>}
                          </td>
                          <td className="py-1.5 pr-3 text-gray-400 font-mono truncate max-w-[140px]">{r.model || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {audit && audit.total > audit.rows.length && (
                  <p className="text-[11px] text-gray-400 mt-2">{audit.rows.length} of {audit.total.toLocaleString()} records · every AI answer is logged, PII-redacted, append-only.</p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  )
}
